/* Ficha de un proceso — el expediente con el rail de decisión al lado (pantalla 2b del rediseño).
 *
 * La ficha se abre para DECIDIR, no para leer. Por eso el veredicto vive en un rail pegajoso a la
 * izquierda: la lectura del expediente es larga y, si el veredicto se fuera con el desplazamiento,
 * habría que subir cada vez que algo lo pone en duda.
 *
 * Lo que hace única a esta pantalla son los interruptores de corrección: la afinidad se calcula con
 * los contratos de la empresa en SECOP y con su RUP, y esos datos no siempre la reflejan. Mover un
 * interruptor PREGUNTA —`POST …/simular`, que no guarda nada— y «Guardar en mi perfil» es otra
 * acción, que además reordena toda la bandeja. Ninguna de las dos recalcula la fórmula aquí.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { enviar, ErrorApi, obtener } from '../api/cliente'
import {
  decimal, diasHasta, entero, ETIQUETA_BANDA, fecha, fechaCorta, frasesDeCambios, haceCuanto,
  normalizarNombre, normalizarObjeto, pesos, plazo, plazoEsCuenta, porcentaje, urlSegura,
} from '../api/formato'
import { BLOQUES, bloqueDe, repartoDe } from '../api/documentos'
import { partirCriterios } from '../api/tipos'
import type {
  AnalisisPrevio, BandaPrecios, CambioProceso, Criterio, Documento, Perfil, Proceso,
  RespuestaFicha, Simulacion, Veredicto,
} from '../api/tipos'
import { usePerfil } from '../estado/perfil'
import { QuePaso } from '../ui/QuePaso'
import { usarAngosto } from '../ui/usarAngosto'
import {
  IconoAviso, IconoExterno, IconoNo, IconoQuitar, IconoSi, IconoSinDato, IconoVolver,
} from '../ui/Iconos'
import { BandaPosicion, BarrasReparto, LineaTiempo } from '../ui/Graficos'
import './Ficha.css'

/** El lote de la bandeja al que pertenece esta ficha, tal como la bandeja lo ordenó. */
type Recorrido = { ids: string[]; total: number }

/** Siguiente o anterior en el lote. `j` es +1, `k` es −1. Null en los extremos o sin recorrido. */
export function pasoRecorrido(ids: string[], actual: string, paso: number): string | null {
  const i = ids.indexOf(actual)
  if (i < 0) return null
  return ids[i + paso] ?? null
}

/** Los cuatro interruptores del rediseño, con el delta de perfil que propone cada uno.
 *  El texto se arma con datos del proceso porque «$1.265 millones caben en mi capacidad» dice algo
 *  y «ajustar capacidad» no dice nada. */
type Interruptor = {
  clave: string
  etiqueta: string
  puesto: (p: Perfil) => boolean
  delta: (p: Perfil) => Partial<Perfil>
}

export function Ficha() {
  const { id } = useParams()
  /* El RECORRIDO del que forma parte este proceso: la lista de ids tal como la ordenó la bandeja.
     Viaja entero y no un solo paso a propósito — con `{pos, siguiente}` la cadena moría en el
     primer salto y la segunda ficha decía «3 de 68» sin poder avanzar, que es una barra que
     promete un recorrido y no lo da. Es opcional: por enlace directo no hay recorrido, y entonces
     la barra se pinta sin contador y sin pasos. */
  const recorrido = (useLocation().state ?? null) as Recorrido | null
  const iAqui = recorrido?.ids?.indexOf(id ?? '') ?? -1
  const sitio = recorrido && iAqui >= 0
    ? {
        pos: iAqui + 1,
        total: recorrido.total,
        anterior: iAqui > 0 ? recorrido.ids[iAqui - 1] ?? null : null,
        siguiente: recorrido.ids[iAqui + 1] ?? null,
      }
    : null
  const { perfil, editar, marcar, registrarResultado } = usePerfil()
  const angosto = usarAngosto()
  const navegar = useNavigate()

  /* Las mismas J/K de la bandeja: pasar a la ficha siguiente o anterior del lote. Sin recorrido
     (enlace directo) no hay a dónde ir. No se disparan mientras se escribe, igual que allá. */
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      const foco = document.activeElement
      const escribiendo = foco instanceof HTMLElement
        && (['INPUT', 'TEXTAREA', 'SELECT'].includes(foco.tagName) || foco.isContentEditable)
      if (escribiendo || e.metaKey || e.ctrlKey || e.altKey || !id) return
      const tecla = e.key.toLowerCase()
      const paso = tecla === 'j' ? 1 : tecla === 'k' ? -1 : 0
      if (!paso) return
      const dest = pasoRecorrido(recorrido?.ids ?? [], id, paso)
      if (!dest) return
      e.preventDefault()
      navegar(`/proceso/${dest}`, { state: recorrido })
    }
    window.addEventListener('keydown', alPulsar)
    return () => window.removeEventListener('keydown', alPulsar)
  }, [recorrido, id, navegar])

  const [datos, setDatos] = useState<RespuestaFicha | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ajustes, setAjustes] = useState<Partial<Perfil>>({})
  const [simulado, setSimulado] = useState<Veredicto | null>(null)
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    if (!id) return
    const corte = new AbortController()
    setAjustes({})
    setSimulado(null)
    obtener<RespuestaFicha>(`/api/oportunidades/${id}`, corte.signal)
      .then((r) => {
        setDatos(r)
        setError(null)
      })
      .catch((e: unknown) => {
        if (corte.signal.aborted) return
        setError(e instanceof ErrorApi ? e.message : String(e))
      })
    return () => corte.abort()
  }, [id])

  const proceso = datos?.procesos?.[0]
  /* Qué se sabe ya del pliego. `null` = nadie lo ha leído, y entonces la ficha ofrece analizarlo.
     Viaja dentro de la ficha, no en una llamada aparte: si no, el botón diría «Analizar el pliego»
     y cambiaría a «Ver el análisis» medio segundo después, delante de quien iba a pulsarlo. */
  const previo = datos?.analisis_previo ?? null
  const entidad = proceso ? datos?.entidades?.[proceso.entidad_nit] : undefined
  const ajustado = Object.keys(ajustes).length > 0
  const veredicto = simulado ?? datos?.veredicto ?? null

  const interruptores = useMemo<Interruptor[]>(() => {
    if (!proceso) return []
    const dep = entidad?.departamento
    const millones = Math.ceil((proceso.precio_base ?? 0) / 1e6)
    // Los días que QUEDAN, no `dias_plazo`: ese campo lo calcula el build y es la ventana
    // completa desde la publicación. Preguntarle a alguien si puede armar la oferta «en 37 días»
    // cuando le quedan 4 es preguntarle por un plazo que ya no existe.
    const dias = diasHasta(proceso.fecha_cierre) ?? 0
    const lista: Interruptor[] = [
      {
        clave: 'experiencia',
        etiqueta: 'Tengo experiencia en este tema',
        puesto: (p) => (p.temas_manuales ?? []).includes(proceso.sector),
        delta: (p) => ({
          temas_manuales: (p.temas_manuales ?? []).includes(proceso.sector)
            ? (p.temas_manuales ?? []).filter((t) => t !== proceso.sector)
            : [...(p.temas_manuales ?? []), proceso.sector],
          sectores: (p.sectores ?? []).includes(proceso.sector)
            ? p.sectores
            : [...(p.sectores ?? []), proceso.sector],
        }),
      },
    ]
    if (dep) {
      lista.push({
        clave: 'territorio',
        etiqueta: `Trabajo en ${dep}`,
        puesto: (p) => (p.deps ?? []).includes(dep),
        delta: (p) => ({
          deps: (p.deps ?? []).includes(dep)
            ? (p.deps ?? []).filter((d) => d !== dep)
            : [...(p.deps ?? []), dep],
        }),
      })
    }
    if (millones > 0) {
      lista.push({
        clave: 'capacidad',
        etiqueta: `${pesos(proceso.precio_base)} caben en mi capacidad`,
        puesto: (p) => !p.max || p.max >= millones,
        delta: (p) => ({ max: !p.max || p.max >= millones ? millones - 1 : millones }),
      })
    }
    if (dias > 0) {
      lista.push({
        clave: 'tiempo',
        etiqueta: `Puedo armar la oferta en ${dias} ${dias === 1 ? 'día' : 'días'}`,
        puesto: (p) => !p.dias_min_oferta || p.dias_min_oferta <= dias,
        delta: (p) => ({
          dias_min_oferta: !p.dias_min_oferta || p.dias_min_oferta <= dias ? dias + 1 : dias,
        }),
      })
    }
    return lista
  }, [proceso, entidad])

  /** El perfil que resultaría de los ajustes en curso. */
  const perfilAjustado = useMemo(() => ({ ...perfil, ...ajustes }), [perfil, ajustes])

  const alternar = useCallback(
    (t: Interruptor) => {
      const cambios = { ...ajustes, ...t.delta(perfilAjustado) }
      setAjustes(cambios)
      if (!id) return
      enviar<Simulacion>(`/api/oportunidades/${id}/simular`, { cambios })
        .then((s) => setSimulado(s.despues))
        .catch(() => setSimulado(null))
    },
    [ajustes, perfilAjustado, id],
  )

  const restablecer = useCallback(() => {
    setAjustes({})
    setSimulado(null)
  }, [])

  const guardar = useCallback(async () => {
    setGuardando(true)
    editar(ajustes)
    setAjustes({})
    // El veredicto simulado pasa a ser el real: la próxima carga de la ficha ya lo traerá así.
    setGuardando(false)
  }, [ajustes, editar])

  if (error) {
    return (
      <div className="andamio">
        <h1>No pude abrir el proceso</h1>
        <p>{error}</p>
        <p>
          <Link to="/">Volver a las oportunidades</Link>
        </p>
      </div>
    )
  }
  if (!datos || !proceso) return <p className="rotulo">Cargando</p>

  const marca = perfil.watch?.[proceso.id]?.estado ?? null
  const nota = perfil.watch?.[proceso.id]?.nota ?? null
  const { aFavor, contra } = partirCriterios(veredicto?.criterios ?? [])

  /* El «qué es esto» del proceso: entidad, objeto y los datos duros.
   *
   * Va en una variable porque en angosto cambia de SITIO, no de aspecto: el documento de móvil pone
   * el hero ANTES de la tarjeta de decisión —primero qué es, después si te sirve—, y en escritorio
   * vive dentro del expediente, a la derecha del rail. Es el mismo patrón que el panel de la
   * bandeja: un solo árbol de marcado montado en uno de dos sitios, en vez de dos ramas que con el
   * tiempo se separan. */
  const cabecera = (
    <header className="exp-cabeza">
      {/* Caja de nombre, con el original en el `title`: la fuente lo trae gritado y el
          diseño lo escribe «Alcaldía de Rionegro». */}
      <p className="rotulo exp-sub" title={entidad?.nombre ?? proceso.entidad_nit}>
        {[normalizarNombre(entidad?.nombre) || proceso.entidad_nit,
          entidad?.ciudad, entidad?.orden].filter(Boolean).join(' · ')}
      </p>
      {/* Muchas entidades escriben el objeto entero en MAYÚSCULAS y otras no, así que la
          misma pantalla mezclaba las dos cosas y las gritadas se leían como un muro. Se
          normaliza a frase recuperando siglas, códigos y los nombres propios que vienen en
          los DATOS (la entidad, su municipio, su departamento). El original se conserva en
          el `title`: esto mejora la lectura, no reemplaza al dato. */}
      <h1 title={proceso.descripcion || proceso.objeto}>
        {normalizarObjeto(proceso.descripcion || proceso.objeto,
                          [entidad?.ciudad, entidad?.departamento])}
      </h1>
      {/* El filete teal de 2 px con los datos duros debajo. Es el ancla de la página: separa
          «de qué va esto» de «cuánto, cuándo y cómo», y hasta ahora esos seis datos estaban
          repartidos entre una línea de subtítulo y tres secciones más abajo. */}
      <dl className="exp-claves">
        <Clave k="Presupuesto" v={pesos(proceso.precio_base)} />
        <Clave k="Cierre de ofertas"
               v={`${fechaCorta(proceso.fecha_cierre)} · ${plazo(proceso.fecha_cierre).toLowerCase()}`} />
        {proceso.duracion != null && (
          <Clave k="Duración"
                 v={`${entero(proceso.duracion)} ${proceso.unidad_duracion ?? 'días'}`} />
        )}
        {/* «Plazo para ofertar» y no «Duración»: el cubo NO trae la duración de ejecución
            —`dias_plazo` es lo que queda para presentar— y el documento de móvil dibuja
            «Duración» porque supone un dato que aquí no existe. Se dice lo que se sabe. */}
        {proceso.dias_plazo != null && (
          <Clave k="Plazo para ofertar" v={`${entero(proceso.dias_plazo)} días`}
                 clase="solo-angosto" />
        )}
        <Clave k="Modalidad" v={proceso.modalidad} />
        {proceso.referencia && (
          <Clave k="Referencia" v={proceso.referencia} clase="no-angosto" />
        )}
      </dl>
    </header>
  )

  return (
    <div className="ficha">
      {/* ── BARRA DE RECORRIDO ─────────────────────────────────────────────
          La cabecera del panel en 2b: volver a la izquierda y el recorrido a la derecha. Sustituye
          a las migas —que decían dónde estás y nada más— porque además DEJA PASAR: revisar un lote
          de la bandeja es abrir una ficha, decidir y pasar a la siguiente, y volver a la lista para
          eso son dos clics y una lista que hay que volver a leer.

          El contador y los pasos dependen de haber llegado DESDE la bandeja: el sitio viaja en el
          estado del router porque es contexto de esa lista —con esos filtros y ese orden—, no una
          propiedad del proceso. Quien abre un enlace compartido no forma parte de ninguna lista, y
          entonces aquí solo queda volver. Inventarle un «2 de 68» sería decirle algo que no es
          suyo.

          En los extremos el paso que falta NO desaparece: se apaga. Si se fuera, la barra cambiaría
          de forma al llegar al primero o al último del lote y el sitio del cursor dejaría de ser el
          mismo de una ficha a otra.

          Los dos pasos están a TODOS los anchos por petición del usuario (18-ago-2026): el 8b del
          documento de móvil solo trae «siguiente», y eso queda derogado. En angosto lo que cambia
          es la forma —los pasos se quedan en el signo, que es lo que cabe—, no lo que se puede
          hacer. */}
      <nav className="ficha-barra" aria-label="Recorrido">
        <Link className="ficha-volver" to="/">
          <IconoVolver />
          {/* Un solo enlace con dos rótulos: en escritorio «Volver a la bandeja», y en angosto solo
              «Bandeja», que es lo que cabe al lado del recorrido. A cada ancho sobra uno, y lo
              apaga el CSS. */}
          <span className="ficha-volver-largo">Volver a la bandeja</span>
          <span className="ficha-volver-corto">Bandeja</span>
        </Link>
        {sitio && (
          <span className="ficha-recorrido">
            <span className="ficha-sitio">
              {entero(sitio.pos)} de {entero(sitio.total)}
            </span>
            <Paso sentido="atras" a={sitio.anterior} recorrido={recorrido} />
            <Paso sentido="adelante" a={sitio.siguiente} recorrido={recorrido} />
          </span>
        )}
      </nav>

      {/* La franja de veredicto que el documento pide pegada arriba mientras se lee el expediente:
          es el rail reducido a su primera línea. Solo existe por debajo de 640, donde el rail deja
          de ser columna y queda al principio de un documento de varias pantallas. */}
      {veredicto?.banda && veredicto.afinidad !== null && (
        <div className="ficha-franja">
          <span className={`rail-banda b-${veredicto.banda}`}>
            {ETIQUETA_BANDA[veredicto.banda]}
          </span>
          <span className="ficha-franja-cifra">
            <b className="cifra">{veredicto.afinidad}</b><span>/100</span>
          </span>
          {/* Con su verbo, como la fila de la bandeja: era la única cifra de plazo del producto
              que se quedaba en «5 días» a secas. El prefijo solo cuando el plazo es una cuenta —en
              los bordes `plazo()` ya dice «Cierra hoy» o «Cerrado». */}
          <span className="ficha-franja-cierre">
            {plazoEsCuenta(proceso.fecha_cierre) ? 'cierra en ' : ''}
            {plazo(proceso.fecha_cierre).toLowerCase()}
          </span>
        </div>
      )}

      {angosto && cabecera}

      <div className="ficha-cuerpo">
        {/* ── RAIL DE DECISIÓN ───────────────────────────────────────────── */}
        <aside className="rail" aria-label="Veredicto">
          <div className="rail-bloque">
            <div className="rail-cabeza">
              {/* La banda va como TEXTO en versalita del color de la banda, no como píldora: la
                  píldora competía con la cifra de 52 px que viene justo debajo. */}
              {veredicto?.banda && veredicto.afinidad !== null ? (
                <span className={`rail-banda b-${veredicto.banda}`}>
                  {ETIQUETA_BANDA[veredicto.banda]}
                </span>
              ) : (
                <span className="rail-sin-veredicto">Todavía sin afinidad</span>
              )}
              {ajustado && <span className="ajustado">Ajustado por ti</span>}
            </div>

            {veredicto?.banda && veredicto.afinidad !== null && (
              <>
                <div className="rail-cifra">
                  <span className="cifra">{veredicto.afinidad}</span>
                  <span>/100 de afinidad</span>
                </div>
                <div className="rail-barra" aria-hidden="true">
                  <i className={`b-${veredicto.banda}`} style={{ width: `${veredicto.afinidad}%` }} />
                </div>
              </>
            )}
            <p className="rail-prosa">{veredicto?.prosa}</p>
          </div>

          {(aFavor.length > 0 || contra.length > 0) && (
            <>
              <hr className="filete" />
              <div className="rail-criterios">
                {aFavor.length > 0 && (
                  <>
                    <h2 className="rotulo">A favor</h2>
                    <ListaCriterios items={aFavor} />
                  </>
                )}
                {contra.length > 0 && (
                  <>
                    <h2 className="rotulo rail-contra">En contra</h2>
                    <ListaCriterios items={contra} />
                  </>
                )}
              </div>
            </>
          )}

          {interruptores.length > 0 && (
            <div className="corrige">
              <h2 className="rotulo">¿No es así? Corrígelo</h2>
              <p className="corrige-nota">
                La afinidad se calcula con tus contratos en SECOP y tu RUP. Si algo de eso no refleja
                a tu empresa, cámbialo y el veredicto se recalcula.
              </p>
              {interruptores.map((t) => {
                const puesto = t.puesto(perfilAjustado)
                return (
                  <button key={t.clave} type="button" className="interruptor"
                          aria-pressed={puesto} onClick={() => alternar(t)}>
                    <span className={`palanca${puesto ? ' puesta' : ''}`} aria-hidden="true">
                      <i />
                    </span>
                    <span>{t.etiqueta}</span>
                  </button>
                )
              })}
              {ajustado && (
                <div className="corrige-acciones">
                  <button type="button" className="boton-primario" disabled={guardando}
                          onClick={() => void guardar()}>
                    Guardar en mi perfil
                  </button>
                  <button type="button" className="accion" onClick={restablecer}>
                    Restablecer
                  </button>
                  {/* Guardar no afecta solo a esta ficha: hay que decirlo antes, no después. */}
                  <p className="corrige-nota">
                    Guardarlo cambia el orden de todas tus oportunidades, no solo esta.
                  </p>
                </div>
              )}
            </div>
          )}

          <hr className="filete" />

          {/* Esto afirmaba «Nadie ha leído los pliegos de este proceso» SIEMPRE, incluso justo
              después de que alguien acabara de leerlos: el informe vivía en el estado de otra
              pantalla y no dejaba rastro. Ahora se persiste (`app/corpus.py`) y la ficha puede
              decir la verdad, que es una de dos cosas distintas. */}
          <div className="rail-bloque">
            {previo ? (
              <>
                <h2 className="rotulo">Lo que dice el pliego</h2>
                <ResumenAnalisis previo={previo} />
              </>
            ) : (
              <>
                <h2 className="rotulo">Lo que todavía no sabes</h2>
                <p className="corrige-nota">
                  Nadie ha leído los pliegos de este proceso, así que los requisitos habilitantes
                  están sin revisar. Es lo primero que podría cambiar este veredicto.
                </p>
              </>
            )}
          </div>

          {/* En el orden del diseño: la acción que resuelve «lo que todavía no sabes» va primera,
              primaria y a todo el ancho. Antes eran tres secundarias en fila y la primaria vivía
              al final del expediente, a varias pantallas de aquí. */}
          <div className="rail-acciones">
            {/* Ya analizado ⇒ verlo NO es la acción principal (ya está hecho) y sobre todo NO
                promete un minuto: es instantáneo y no cuesta nada. */}
            <Link className={previo ? 'accion' : 'boton-primario'}
                  to={`/proceso/${proceso.id}/pliego`}>
              {previo ? 'Ver el análisis del pliego' : 'Analizar el pliego · 1 min'}
            </Link>
            {urlSegura(proceso.url) && (
              <a className="accion" href={urlSegura(proceso.url)!} target="_blank" rel="noreferrer">
                Ficha oficial en SECOP <IconoExterno />
              </a>
            )}
            <div className="par">
              <button type="button" className={`accion${marca === 'sigo' ? ' puesta' : ''}`}
                      aria-pressed={marca === 'sigo'}
                      onClick={() => marcar(proceso.id, marca === 'sigo' ? null : 'sigo')}>
                {marca === 'sigo' ? 'Siguiendo' : 'Seguir'}
              </button>
              <button type="button" className={`accion${marca === 'desc' ? ' puesta' : ''}`}
                      aria-pressed={marca === 'desc'}
                      onClick={() => marcar(proceso.id, marca === 'desc' ? null : 'desc',
                                            marca === 'desc' ? undefined : 'No me interesa')}>
                {marca === 'desc' ? 'Descartado' : 'Descartar'}
              </button>
            </div>
            <QuePaso
              actual={perfil.watch?.[proceso.id]?.resultado}
              onElegir={(r) => registrarResultado(proceso.id, r)}
            />
            {/* Lleva el proceso COMO CONTEXTO. Desde el asistente se vuelve aquí, no al listado:
                quien vino a decidir sobre este proceso vuelve a donde estaba decidiendo. */}
            <Link className="accion" to={`/asistente?proceso=${encodeURIComponent(proceso.id)}`}>
              Preguntar al asistente sobre este proceso
            </Link>
          </div>

          {/* Lo que se guardó desde el asistente con «Guardar en la ficha». Va en el rail, junto al
              veredicto, porque es lo que quien volvió a esta ficha quiso recordar de ella. */}
          {nota && (
            <div className="rail-bloque">
              <h2 className="rotulo">Lo que guardaste del asistente</h2>
              <p className="rail-nota">{nota}</p>
            </div>
          )}

          {/* Descargo del rail. No es decorado: la afinidad mide PARECIDO con lo que la empresa hace,
              y quien ve un 91 tiende a leerlo como probabilidad de ganar. */}
          <p className="descargo">
            La afinidad mide cuánto se parece este proceso a tu empresa, no la probabilidad de que lo
            ganes.
          </p>
        </aside>

        {/* ── EXPEDIENTE ─────────────────────────────────────────────────── */}
        <article className="expediente">
          {!angosto && cabecera}

          <Seccion n={2} titulo="Cuánto ofertar">
            <BandaPrecios datos={datos} proceso={proceso} />
          </Seccion>

          <Seccion n={3} titulo="Te alcanza el tiempo">
            <dl className="datos">
              <Dato k="Días para presentar" v={plazo(proceso.fecha_cierre)} />
              <Dato k="Publicado" v={fecha(proceso.fecha_publicacion)} />
              <Dato k="Cierra" v={fecha(proceso.fecha_cierre)} />
              {proceso.plazo_mediana_modalidad != null && (
                <Dato k="Mediana de su modalidad"
                      v={`${entero(proceso.plazo_mediana_modalidad)} días`} />
              )}
              {proceso.numero_lotes != null && proceso.numero_lotes > 1 && (
                <Dato k="Lotes" v={`${proceso.numero_lotes} — puedes ofertar por uno`} />
              )}
            </dl>
            <LineaTiempo publicado={proceso.fecha_publicacion} cierre={proceso.fecha_cierre} />
            {/* Duración del contrato y plazo para ofertar son cosas distintas y hay que mantenerlas
                distinguibles: una es cuánto tienes para preparar, la otra cuánto tendrías que
                sostener la ejecución. */}
            {proceso.duracion != null && (
              <p className="nota">
                Una vez adjudicado, el contrato dura {proceso.duracion}{' '}
                {proceso.unidad_duracion ?? 'días'}. No lo confundas con el plazo para ofertar.
              </p>
            )}
          </Seccion>

          <Seccion n={4} titulo="Contra quién compites">
            <dl className="datos">
              {proceso.oferentes_esperados != null && (
                <Dato k="Oferentes esperados"
                      v={`${decimal(proceso.oferentes_esperados)} (sobre ${entero(proceso.oferentes_esperados_n, '0')} procesos)`} />
              )}
              {proceso.p_oferente_unico != null && (
                <Dato k="Probabilidad de oferente único"
                      v={porcentaje(proceso.p_oferente_unico, { fraccion: true })} />
              )}
            </dl>
            <Competidores datos={datos} proceso={proceso} />
          </Seccion>

          <Seccion n={5} titulo="Cómo es trabajar con esta entidad">
            <ComoCompra entidad={entidad} />
          </Seccion>

          <Seccion n={6} titulo="Documentos del proceso"
                   titular={tituloDocumentos(proceso.documentos)}>
            <Documentos documentos={proceso.documentos} cambios={proceso.cambios}
                        procesoId={proceso.id} />
          </Seccion>

          {/* Caja destacada y no una sección más: es la única ACCIÓN del expediente, y entre seis
              secciones seguidas una sección más se lee como información, no como algo que hacer. */}
          <div className="exp-pliego">
            <div>
              <span className="rotulo">07 · Análisis del pliego</span>
              {previo ? (
                <>
                  <h2>Ya leímos este pliego</h2>
                  <p className="exp-pliego-cuando">
                    {entero(previo.n_documentos)}{' '}
                    {previo.n_documentos === 1 ? 'documento' : 'documentos'}, {haceCuanto(previo.analizado_en)}.
                    {previo.desactualizado
                      ? ' Desde entonces el proceso publicó documentos nuevos.'
                      : ''}
                  </p>
                  <ResumenAnalisis previo={previo} detallado />
                </>
              ) : (
                <>
                  <h2>Qué te van a exigir, con las citas del pliego</h2>
                  <p>
                    Lee los documentos del proceso y te dice qué te van a exigir (experiencia,
                    índices financieros, requisitos técnicos), qué debes preparar y si hay
                    requisitos que parecen hechos a la medida de otro. Tarda un minuto o dos.
                  </p>
                </>
              )}
            </div>
            <Link className={previo ? 'accion' : 'boton-primario'}
                  to={`/proceso/${proceso.id}/pliego`}>
              {previo ? 'Ver el análisis' : 'Analizar el pliego · 1 min'}
            </Link>
          </div>

          {/* Descargo final. La regla de tono del producto, y va textual: todo lo que se muestra
              del convocante describe su contratación PASADA. */}
          <p className="descargo">
            Todo lo que aparece aquí sobre la entidad describe su contratación pasada según los
            datos abiertos de SECOP II: son antecedentes para decidir dónde competir, no una
            calificación de la entidad ni una acusación. Verifica fechas, pliegos y estado en la
            ficha oficial. Generado el {fecha(new Date().toISOString())}.
          </p>
        </article>
      </div>

      {/* Pie de acciones para pantalla estrecha. En móvil el expediente es larguísimo y las
          acciones viven al final del rail, arriba del todo: quien termina de leer tendría que
          desplazarse hasta el principio para hacer algo. Pegado abajo están donde se decide.
          Solo se muestra por CSS bajo 1000 px — en escritorio el rail ya las tiene a la vista. */}
      <div className="ficha-pie-movil">
        <Link className="boton-primario" to={`/proceso/${proceso.id}/pliego`}>
          {previo ? 'Ver el análisis' : 'Analizar el pliego'}
        </Link>
        <button type="button" className={`accion${marca === 'sigo' ? ' puesta' : ''}`}
                aria-pressed={marca === 'sigo'}
                onClick={() => marcar(proceso.id, marca === 'sigo' ? null : 'sigo')}>
          {marca === 'sigo' ? 'Siguiendo' : 'Seguir'}
        </button>
        <button type="button" className={`accion accion-icono${marca === 'desc' ? ' puesta' : ''}`}
                aria-pressed={marca === 'desc'}
                aria-label={marca === 'desc' ? 'Quitar el descarte' : 'Descartar este proceso'}
                onClick={() => marcar(proceso.id, marca === 'desc' ? null : 'desc',
                                      marca === 'desc' ? undefined : 'No me interesa')}>
          <IconoQuitar />
        </button>
      </div>
    </div>
  )
}

const PASOS = {
  atras: { clase: 'ficha-anterior', rotulo: 'anterior', signo: '‹' },
  adelante: { clase: 'ficha-siguiente', rotulo: 'siguiente', signo: '›' },
} as const

/** Un paso del recorrido: «‹ anterior» o «siguiente ›».
 *
 * `state={recorrido}` no es un adorno: el recorrido es lo ÚNICO que sostiene el contador, y sin
 * reenviarlo la cadena muere en el primer salto —la ficha siguiente ya no sabría de qué lote es y
 * la barra se quedaría solo con «Volver a la bandeja».
 *
 * Sin destino el paso se apaga, no se quita. En el primero y en el último del lote la barra tiene
 * que seguir midiendo lo mismo y los rótulos quedarse donde estaban.
 *
 * El rótulo va en su propio nodo porque en angosto se apaga y queda solo el signo: a 390 px no
 * caben las dos palabras (medido). El `aria-label` lo dice igual a cualquier ancho, así que ahí no
 * se pierde nada.
 */
function Paso(
  { sentido, a, recorrido }:
  { sentido: keyof typeof PASOS; a: string | null; recorrido: Recorrido | null },
) {
  const { clase, rotulo, signo } = PASOS[sentido]
  const flecha = <span aria-hidden="true">{signo}</span>
  const rotuloNodo = <span className="ficha-paso-rotulo">{rotulo}</span>
  const dentro: ReactNode = sentido === 'atras'
    ? <>{flecha}{rotuloNodo}</>
    : <>{rotuloNodo}{flecha}</>
  const clases = `ficha-paso ${clase}`
  if (!a) {
    return (
      <span className={`${clases} ficha-paso-inerte`} aria-label={rotulo} aria-disabled="true">
        {dentro}
      </span>
    )
  }
  return (
    <Link className={clases} to={`/proceso/${a}`} state={recorrido} aria-label={rotulo}>
      {dentro}
    </Link>
  )
}

function ListaCriterios({ items }: { items: Criterio[] }) {
  return (
    <ul className="criterios">
      {items.map((c, i) => (
        <li key={i} className={`criterio criterio-${c.signo}`}>
          <Icono signo={c.signo} />
          <span>{c.texto}</span>
        </li>
      ))}
    </ul>
  )
}

function Icono({ signo }: { signo: Criterio['signo'] }) {
  if (signo === 'ok') return <IconoSi />
  if (signo === 'no') return <IconoNo />
  if (signo === 'alerta') return <IconoAviso />
  return <IconoSinDato />
}

/**
 * Una sección del expediente, precedida por su REGLA.
 *
 * La línea va como elemento propio y no como `border-top` de la sección: el diseño separa con
 * línea Y con 30 px de aire a cada lado, y un borde se come el aire de un lado. Sin esas reglas
 * las seis secciones se leían como un solo bloque largo — que fue exactamente el reporte.
 */
/**
 * «Cuatro para decidir, once para preparar la oferta y siete de trámite» — el reparto en una frase.
 *
 * Es el titular de la sección, y sale de los datos. El documento lo escribe como una división en
 * dos («Diez para decidir, doce administrativos») porque su ejemplo no tiene la mitad de en medio;
 * en los procesos reales sí la hay —los ANEXO 7, 9, 13 que hay que DILIGENCIAR y adjuntar— y
 * meterlos en cualquiera de las dos puntas sería mentir: ni sirven para decidir si te presentas ni
 * son papeleo ajeno.
 *
 * Solo se nombra lo que existe: un proceso sin trámite no dice «y 0 de trámite».
 */
function repartoEnPalabras(decidir: number, preparar: number, tramite: number): string {
  const partes: string[] = []
  if (decidir) partes.push(`${entero(decidir)} ${decidir === 1 ? 'documento' : 'documentos'} para decidir`)
  if (preparar) partes.push(`${entero(preparar)} para preparar la oferta`)
  if (tramite) partes.push(`${entero(tramite)} de trámite`)
  if (!partes.length) return 'Sin documentos publicados'
  const ultima = partes.pop() as string
  return capitalizarFrase(partes.length ? `${partes.join(', ')} y ${ultima}` : ultima)
}

const capitalizarFrase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/** El titular de la sección 06, ya escrito. */
function tituloDocumentos(docs: Documento[] | undefined): string {
  const r = repartoDe(docs)
  return repartoEnPalabras(r.decidir, r.preparar, r.tramite)
}


/* ── el análisis del pliego, resumido ─────────────────────────────────────── */

/**
 * Lo que se sabe del pliego, en la ficha y sin abrir el informe.
 *
 * El COLOR lo decide si TÚ cumples, no la severidad del requisito. Es la corrección que motivó
 * todo esto: un pliego que exige liquidez ≥ 1,8 se pintaba en rojo delante de una empresa cuya
 * liquidez es 112,58, porque el color venía de «esto es exigente» y no de «esto te deja fuera».
 * Son dos preguntas distintas y la ficha responde la segunda; la primera vive en el informe, en su
 * propia línea, porque un umbral que tú superas puede seguir restringiendo la competencia.
 *
 * Los `sin_dato` NO cuentan como incumplidos —«no pudimos verificarlo» no es «no cumples»— pero
 * tampoco se esconden: se dicen aparte, porque son lo que le falta a tu perfil, no al pliego.
 */
function ResumenAnalisis({ previo, detallado = false }: {
  previo: AnalisisPrevio
  detallado?: boolean
}) {
  const { ok, no, sin_dato: sinDato } = previo.cumple
  const total = ok + no + sinDato
  const riesgos = previo.riesgos.alta + previo.riesgos.media + previo.riesgos.baja

  return (
    <div className="resumen-pliego">
      {total > 0 ? (
        <p className={`resumen-veredicto ${no > 0 ? 'no' : 'ok'}`}>
          {no > 0 ? <IconoNo /> : <IconoSi />}
          <span>
            {no > 0
              ? <>No cumples {entero(no)} de {entero(total)} requisitos habilitantes</>
              : <>Cumples los {entero(ok)} requisitos que pudimos verificar</>}
          </span>
        </p>
      ) : (
        <p className="resumen-veredicto sin-dato">
          <IconoSinDato />
          <span>El pliego no trae requisitos habilitantes que podamos cruzar con tu perfil.</span>
        </p>
      )}

      {previo.incumplidos.length > 0 && (
        <ul className="resumen-incumplidos">
          {previo.incumplidos.slice(0, detallado ? 6 : 2).map((r) => (
            <li key={r.etiqueta}>
              <b>{r.etiqueta}</b>: exige {r.exigido} · {r.tuyo}
              {detallado && r.arreglo && <span className="resumen-arreglo">{r.arreglo}</span>}
            </li>
          ))}
        </ul>
      )}

      {sinDato > 0 && (
        <p className="resumen-sindato">
          {entero(sinDato)} {sinDato === 1 ? 'requisito' : 'requisitos'} sin verificar: no tenemos
          ese dato de tu empresa. Cárgalo en tu perfil y el cruce se completa.
        </p>
      )}

      {riesgos > 0 && (
        // En su PROPIA línea y separado del cumplimiento a propósito: son señales sobre el pliego
        // —posible restricción de la competencia—, no sobre si tú puedes presentarte.
        <p className="resumen-riesgos">
          {previo.riesgos.alta > 0 && <IconoAviso />}
          <span>
            {entero(riesgos)} {riesgos === 1 ? 'requisito' : 'requisitos'} del pliego{' '}
            {riesgos === 1 ? 'podría' : 'podrían'} estar restringiendo la competencia
            {previo.riesgos.alta > 0 ? ` (${entero(previo.riesgos.alta)} de severidad alta)` : ''}.
          </span>
        </p>
      )}
    </div>
  )
}

/* ── documentos del proceso (pantalla 7a) ──────────────────────────────────────────────────────
 *
 * Era una lista plana de nombre + categoría. El documento 7 la reescribe y su tesis es que los
 * documentos NO son una vista aparte sino una CAPA de la ficha: se leen en el mismo scroll, sin
 * navegar, y ordenados por qué hay que mirar primero.
 *
 * Lo que hace esta sección, y por qué cada cosa:
 *
 * · AGRUPA por lo que un oferente necesita, usando el `grupo` que ya trae la clasificación
 *   heurística de `secop_api.clasificar_documento`. En un proceso real hay veintitantos archivos y
 *   más de la mitad son administrativos; una lista plana entierra el pliego entre actas y CDP.
 * · COLAPSA el ruido —administrativos y sin clasificar— en un pie que dice cuántos son y por qué
 *   podrías querer abrirlos igual. «Sin clasificar» no es «irrelevante»: es un nombre opaco.
 * · AVISA de las adendas Y de lo que el cubo sabe que cambió. Son dos hechos distintos y por eso
 *   van juntos: el conteo de adendas dice que hay un documento que leer; `cambios` —el diff entre
 *   dos corridas del sync— dice QUÉ se movió, con el dato oficial. Lo que NO se hace es atarlos:
 *   «la adenda 2 movió el cierre» sería una atribución que no consta en ningún dato.
 *
 * ⚠ Lo que el diseño dibuja y AQUÍ NO ESTÁ, con su razón: al pulsar un documento, el 7a abre en su
 * sitio la CITA verbatim del archivo («El proponente deberá acreditar…») y los hallazgos que
 * salieron de él. Esos datos existen —`bulk_pliegos.py` extrae 24 columnas `pliego_*` con cita
 * verificada y las proyecta al grafo— pero NO llegan hasta aquí: `olap/build_procesos_abiertos.py`
 * no las incluye en el snapshot (es la fase 8b, que quedó pendiente). Inventar una cita o dejar un
 * desplegable que siempre dice «nadie ha leído esto» sería peor que no ponerlo, así que la fila es
 * directamente accionable y la promesa de las citas la hace «Analizar el pliego», que es quien las
 * puede cumplir hoy.
 *
 * Por lo mismo NO hay un «Preguntar sobre este» por fila, que el 7a sí dibuja: el asistente recibe
 * el contexto del PROCESO, no el del documento, así que cinco enlaces idénticos prometerían algo
 * que la aplicación no cumple. Vuelve cuando el documento pueda viajar como contexto.
 */

function Documentos({ documentos, cambios, procesoId }: {
  documentos?: Documento[]
  cambios?: CambioProceso[] | null
  procesoId: string
}) {
  const [verPie, setVerPie] = useState(false)
  const docs = documentos ?? []
  if (!docs.length) {
    return <p className="nota">Este proceso no tiene documentos publicados todavía.</p>
  }

  // Cada documento cae en UN bloque: el primero cuya categoría lo reclame, o cuyo `grupo` lo
  // recoja. Lo que no reclama nadie va al pie, donde acaban los administrativos y los opacos.
  const arriba = BLOQUES
    .map((b, i) => ({ ...b, docs: docs.filter((d) => bloqueDe(d) === i) }))
    .filter((b) => b.docs.length)
  const abajo = docs.filter((d) => bloqueDe(d) < 0)
  const adendas = docs.filter((d) => d.categoria === 'Adenda')
  /* Lo que el CUBO sabe que cambió, que es distinto de saber que hay adendas: `cambios` compara dos
     corridas del sync y trae el dato oficial —«el cierre pasó del 12 al 19 de agosto»— sin decir
     quién lo movió. Las dos cosas van juntas porque se responden entre sí: el conteo dice que hay
     un documento que leer, el diff dice qué se movió, y ninguna afirma que lo uno causó lo otro. */
  const cambiado = frasesDeCambios(cambios)

  return (
    <>
      <p className="nota">
        En vivo desde SECOP II. La categoría es una <b>heurística por el nombre del archivo</b>:
        ordena qué leer primero, no afirma qué es cada documento. Solo cubre lo publicado{' '}
        <b>desde 2025</b>: si el proceso subió documentos antes, no aparecen aquí.{' '}
        {/* El archivo completo (7c): destino de excepción, a un clic. Aquí están los que sirven
            para decidir; allá están TODOS, con buscador. */}
        <Link to={`/proceso/${procesoId}/documentos`}>
          Archivo completo · {entero(docs.length)}
        </Link>
      </p>

      {/* Las adendas mandan sobre el pliego, así que quien lee la versión vieja pierde. Se dice
          CUÁNTAS hay y qué implica; NO se dice qué cambiaron —el nombre del archivo no lo sabe— y
          fingirlo sería la clase de afirmación que este producto no hace. */}
      {(adendas.length > 0 || cambiado.length > 0) && (
        <p className="docs-adenda">
          <IconoAviso aria-hidden="true" />
          <span>
            {adendas.length > 0 && (
              <>
                Este proceso tiene{' '}
                <b>{entero(adendas.length)} {adendas.length === 1 ? 'adenda' : 'adendas'}</b>.{' '}
              </>
            )}
            {cambiado.length > 0 && (
              <>
                Desde la última revisión, <b>{cambiado.join(' y ')}</b>.{' '}
              </>
            )}
            {adendas.length > 0
              ? 'Una adenda manda sobre el pliego: mira primero la más reciente.'
              : 'Revisa si hay una adenda publicada que lo explique.'}
          </span>
        </p>
      )}

      {arriba.map((b) => (
        <div className="docs-bloque" key={b.titulo}>
          <div className="docs-bloque-cabeza">
            <span className="docs-bloque-titulo">{b.titulo}</span>
            <span className="docs-bloque-nota">{b.nota}</span>
          </div>
          <ul className="docs">
            {b.docs.map((d, i) => <FilaDoc d={d} key={i} />)}
          </ul>
        </div>
      ))}

      {abajo.length > 0 && (
        <div className="docs-pie">
          <p>
            <b>{entero(abajo.length)} de trámite y sin clasificar</b> — actas, CDP, certificaciones
            y nombres opacos. Opaco no es irrelevante: si el pliego no aparece arriba, mira ahí.
          </p>
          <button type="button" className="accion" onClick={() => setVerPie((v) => !v)}
                  aria-expanded={verPie}>
            {verPie ? 'Ocultar' : 'Mostrar'}
          </button>
          {verPie && (
            <ul className="docs">
              {abajo.map((d, i) => <FilaDoc d={d} key={i} />)}
            </ul>
          )}
        </div>
      )}
    </>
  )
}

/**
 * Una fila de documento: nombre, categoría, fecha y las dos acciones.
 *
 * El nombre va tal cual lo publicó la entidad, sin normalizar la caja: es el nombre de un ARCHIVO
 * —el identificador con el que alguien lo va a buscar en SECOP— y no una frase que se compone.
 */
function FilaDoc({ d }: { d: Documento }) {
  const href = urlSegura(d.url)
  return (
    <li className="doc">
      <span className="doc-titulo">
        {href ? (
          <a href={href} target="_blank" rel="noreferrer">
            {d.nombre} <IconoExterno />
          </a>
        ) : (
          <span title="SECOP no publicó el enlace de este archivo">{d.nombre}</span>
        )}
      </span>
      <span className="doc-cat">{d.categoria}</span>
      <span className="doc-fecha cifra">{fechaCorta(d.fecha)}</span>
    </li>
  )
}

/**
 * Una sección del expediente, NUMERADA.
 *
 * Los números los introduce el turno 7 en la ficha («05 · Cómo paga la entidad», «06 · Documentos
 * del proceso»). No son adorno: el expediente son seis bloques de corrido y el número da un asidero
 * para volver a uno —«mira la 06»— y para saber cuánto queda.
 *
 * Con `titular`, el nombre de la sección baja a un rótulo pequeño y el titular grande dice lo que
 * ESTA sección tiene hoy («Diez para decidir, doce administrativos»). Es la jerarquía del 7a y es
 * mejor: el nombre de la sección se sabe leyendo, lo que no se sabe es qué hay dentro.
 */
function Seccion({ n, titulo, titular, children }: {
  n: number
  titulo: string
  titular?: string
  children: React.ReactNode
}) {
  const num = String(n).padStart(2, '0')
  return (
    <>
      <hr className="filete" />
      <section className="exp-seccion">
        {titular ? (
          <>
            <span className="rotulo">{num} · {titulo}</span>
            <h2>{titular}</h2>
          </>
        ) : (
          <h2><span className="exp-num">{num}</span>{titulo}</h2>
        )}
        {children}
      </section>
    </>
  )
}

/** Un dato duro de la cabecera: rótulo pequeño arriba, valor de 17 px debajo. */
/** Un dato duro del expediente.
 *
 * `clase` existe para que la rejilla 2×2 del móvil pueda quedarse con cuatro entradas sin cambiar
 * el árbol: en angosto entra «Plazo para ofertar» y sale «Referencia» —que es lo segundo que la
 * regla de reflow manda sacrificar, y además está en la barra de arriba y en la URL—, y en
 * escritorio ocurre lo contrario. Se hace con CSS y no con una rama de JavaScript para que el
 * marcado sea EL MISMO en los dos anchos: un `display: none` no mueve un solo píxel del
 * escritorio ya aprobado, y dos ramas condicionales sí podrían separarse con el tiempo. */
function Clave({ k, v, clase }: { k: string; v: string; clase?: string }) {
  return (
    <div className={clase}>
      <dt>{k}</dt>
      <dd className="cifra">{v}</dd>
    </div>
  )
}

function Dato({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt>{k}</dt>
      <dd className="cifra">{v}</dd>
    </>
  )
}

export type BloqueOferta = { etiqueta: string; pct: number; detalle: string }

/** Los bloques de «Cuánto ofertar»: convocante y parecidos, etiquetados. Ninguno esconde al otro. */
export function bloquesOferta({ banda, analogos }: {
  banda?: BandaPrecios | null
  analogos?: { n: number; p25_pct: number; p50_pct: number; p75_pct: number } | null
  techo?: number | null
}): BloqueOferta[] {
  const bloques: BloqueOferta[] = []
  if (banda?.mediana_pct != null) {
    const tope = banda.pct_al_tope
    const detalle = tope != null && tope >= 60
      ? `${porcentaje(tope)} de sus adjudicaciones en el sector se cerraron casi al techo.`
      : 'Queda margen de precio: competir por ahí todavía sirve.'
    bloques.push({ etiqueta: 'Este convocante', pct: banda.mediana_pct, detalle })
  }
  if (analogos) {
    bloques.push({
      etiqueta: 'Compras parecidas',
      pct: analogos.p50_pct,
      detalle: `En ${entero(analogos.n)} procesos del mismo tipo y modalidad el ganador ofertó entre ${porcentaje(analogos.p25_pct)} y ${porcentaje(analogos.p75_pct)}.`,
    })
  }
  return bloques
}

/** La banda de precios ganadora del convocante. Se elige con la MISMA jerarquía que el servidor:
 *  entidad×segmento → entidad → segmento nacional. */
function BandaPrecios({ datos, proceso }: { datos: RespuestaFicha; proceso: Proceso }) {
  const seg = (proceso.unspsc || '').replace('V1.', '').slice(0, 2)
  const b =
    datos.banda_precios?.[`${proceso.entidad_nit}|${seg}`] ??
    datos.banda_precios?.[`${proceso.entidad_nit}|*`] ??
    datos.banda_precios?.[seg]
  const an = proceso._analogos
  if (!b && !an) {
    return <p className="nota">Sin historial de adjudicaciones para estimar una banda.</p>
  }
  const techo = proceso.precio_base ?? null
  const bloques = bloquesOferta({ banda: b, analogos: an, techo })
  const disp =
    datos.dispersion_ofertas?.[`${proceso.entidad_nit}|${seg}`] ??
    datos.dispersion_ofertas?.[`${proceso.entidad_nit}|*`] ??
    datos.dispersion_ofertas?.[seg]
  const marcas = bloques.map((bl) => ({
    valor: bl.pct,
    rotulo: bl.etiqueta === 'Compras parecidas' ? 'parecidos' : 'convocante',
  }))
  const zona = an ? { p25: an.p25_pct, p75: an.p75_pct }
    : (b?.p25_pct != null && b.p75_pct != null ? { p25: b.p25_pct, p75: b.p75_pct } : undefined)
  const pie = [
    an ? `${entero(an.n)} compras parecidas` : null,
    b ? `${entero(b.n ?? 0, '0')} adjudicaciones de esta entidad en el sector` : null,
  ].filter(Boolean).join(' · ')
  return (
    <>
      <div className={bloques.length > 1 ? 'oferta-dos' : undefined}>
        {bloques.map((bl) => {
          const objetivo = techo ? (techo * bl.pct) / 100 : null
          return (
            <div key={bl.etiqueta}>
              <p className="rotulo">{bl.etiqueta}</p>
              <p className="exp-hero">
                <b className="cifra">{porcentaje(bl.pct)}</b>{' '}
                {/* Espacio normal ANTES del ≈, para que «del presupuesto» pueda envolver; duro
                    DESPUÉS, porque si no el signo se queda solo al final del renglón y su cifra
                    arranca el siguiente: «del presupuesto ≈» / «$570 millones». */}
                <span>del presupuesto{objetivo ? ` ≈\u00a0${pesos(objetivo)}` : ''}</span>
              </p>
              <p>{bl.detalle}</p>
            </div>
          )
        })}
      </div>
      {marcas.length > 0 && (
        <BandaPosicion marcas={marcas} zona={zona} techo={pesos(techo)} />
      )}
      {disp?.brecha_mediana_pct != null && (
        <p className="nota">
          Entre las ofertas de un mismo proceso la diferencia mediana es de{' '}
          <b>{porcentaje(disp.brecha_mediana_pct, { decimales: 1 })}</b>
          {disp.brecha_mediana_pct < 1
            ? ': se gana por décimas, así que revisa dos veces la aritmética de la propuesta económica.'
            : '.'}
        </p>
      )}
      <p className="nota">{pie} — orientación de mercado, no garantía.</p>
    </>
  )
}

function Competidores({ datos, proceso }: { datos: RespuestaFicha; proceso: Proceso }) {
  const seg = (proceso.unspsc || '').replace('V1.', '').slice(0, 2)
  const top =
    datos.top_proveedores?.[`${proceso.entidad_nit}|${seg}`] ??
    datos.top_proveedores?.[`${proceso.entidad_nit}|*`] ??
    []
  if (!top.length) return <p className="nota">Sin proveedores recurrentes registrados.</p>
  return (
    <>
      <BarrasReparto
        items={top.slice(0, 5).map((t) => ({
          nombre: normalizarNombre(t.nombre),
          valor: t.gasto ?? 0,
          nota: `${t.n ?? 0}× · ${pesos(t.gasto ?? null, 'sin monto')}`,
        }))}
      />
      <p className="nota">
        A quién le ha adjudicado esta entidad en el sector. Que alguien repita no es una
        irregularidad: puede ser el único que cumple los requisitos.
      </p>
    </>
  )
}

/**
 * «Cómo es trabajar con esta entidad»: cómo PAGA y cómo EJECUTA.
 *
 * La velocidad de pago sola no responde el título. Lo que decide si una empresa quiere trabajar con
 * un comprador es también qué le pasa a sus contratos una vez firmados: si se prorrogan, si se
 * suspenden, si terminan costando más. El panel anterior traía esas barras y el rediseño no las
 * dibuja; retirarlas habría sido perder la mitad de la respuesta a la pregunta que encabeza la
 * sección, así que vuelven — los datos ya venían en `entidad.riesgo.ejecucion`.
 *
 * Van en NEUTRO, no en rojo: una prórroga no es una irregularidad. Son antecedentes con los que
 * dimensionar el flujo de caja y el plazo, no una calificación del comprador.
 */
/**
 * «Cómo es trabajar con esta entidad»: cuatro cifras en rejilla 2×2, cada una con su barra y su
 * nota, y un pie con los totales del comprador.
 *
 * Antes era una lista de filas rótulo→valor: la misma información y ninguna jerarquía. El diseño
 * la ordena por lo que decide —cuánto tarda en pagar, cuánto reparte, qué le pasa a sus contratos,
 * cuánto adjudica sin competencia— y pone la magnitud a la vista. Todo va en NEUTRO: una prórroga
 * no es una irregularidad, y esto describe contratación pasada, no califica al comprador.
 */
function ComoCompra({ entidad }: {
  entidad: { riesgo?: Record<string, unknown>; apertura?: { score_0_100?: number } | null } | undefined
}) {
  const riesgo = entidad?.riesgo as {
    pago?: Record<string, number>
    ejecucion?: Record<string, number>
    captura?: Record<string, number>
    dirigido?: Record<string, number>
    base?: Record<string, number>
  } | undefined
  const pago = riesgo?.pago
  const ej = riesgo?.ejecucion
  const base = riesgo?.base
  const captura = riesgo?.captura
  const apertura = entidad?.apertura?.score_0_100
  const sinCompetencia = riesgo?.dirigido?.['pct_gasto_competitivo_oferente_unico']

  if (!pago?.dias_prom && !ej && apertura == null) {
    return <p className="nota">Sin historial registrado para esta entidad.</p>
  }

  // `!= null` no basta: en el cubo estos campos llegan a veces como cadena o como NaN, y entonces
  // `entero()` devuelve su marcador de ausencia y la frase queda en «— años contratando». Un dato
  // que falta se OMITE; no se escribe una frase con un hueco donde iba la cifra.
  const hay = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0
  const totales = [
    hay(base?.['n_contratos']) && `${entero(base!['n_contratos'])} contratos en total`,
    hay(base?.['gasto_total']) && `${pesos(base!['gasto_total'])} de gasto histórico`,
    hay(captura?.['n_proveedores']) && `${entero(captura!['n_proveedores'])} proveedores`,
    hay(base?.['anios']) && `${entero(base!['anios'])} años contratando`,
  ].filter(Boolean) as string[]

  return (
    <>
      <div className="entidad-rejilla">
        {pago?.dias_prom != null && (
          <div className="entidad-dato">
            <p className="exp-hero">
              <b className="cifra">{entero(pago.dias_prom)}</b>
              <span>días en pagar</span>
            </p>
            <div className="entidad-barra" aria-hidden="true">
              <i className="pronto" style={{ width: `${pago.pct_a_60 ?? 0}%` }} />
              <i className="medio"
                 style={{ width: `${Math.max(0, 100 - (pago.pct_a_60 ?? 0) - (pago.pct_mas_90 ?? 0))}%` }} />
              <i className="tarde" style={{ width: `${pago.pct_mas_90 ?? 0}%` }} />
            </div>
            <p className="entidad-nota">
              Desde que recibe la factura. {porcentaje(pago.pct_a_60 ?? 0)} de sus{' '}
              {entero(pago.n, '0')} facturas se pagaron en menos de 60 días. Es velocidad medida, no
              una garantía de pago.
            </p>
          </div>
        )}

        {apertura != null && (
          <div className="entidad-dato">
            <p className="exp-hero">
              <b className="cifra">{entero(apertura)}</b>
              <span>/100 de apertura</span>
            </p>
            <div className="entidad-barra" aria-hidden="true">
              <i className="pronto" style={{ width: `${apertura}%` }} />
            </div>
            {/* El índice pasó de tres componentes a cuatro (ago 2026): entró la rotación de
                ganadores, o sea si el convocante le da trabajo a proveedores que no conocía. La
                nota tiene que decirlo, porque es la mitad que le importa a quien nunca ha
                contratado con él — repartir entre los de siempre no le sirve de nada. */}
            <p className="entidad-nota">
              Alto es bueno: reparte entre proveedores, y no solo entre los que ya conoce.
            </p>
          </div>
        )}

        {ej?.['pct_con_dias_adicionados'] != null && (
          <div className="entidad-dato">
            <p className="exp-hero">
              <b className="cifra">{porcentaje(ej['pct_con_dias_adicionados'], { fraccion: true, decimales: 1 })}</b>
              <span>de sus contratos se prorrogan</span>
            </p>
            <p className="entidad-nota">
              {ej['pct_sobrecosto'] != null && (
                <>Y el {porcentaje(ej['pct_sobrecosto'], { fraccion: true, decimales: 1 })} termina
                  costando más de lo presupuestado. </>
              )}
              {ej['mediana_dias_adicionados']
                ? `Cuando prorroga, lo típico son ${entero(ej['mediana_dias_adicionados'])} días más. `
                : ''}
              {(ej['pct_suspendidos'] ?? 0) === 0 && (ej['pct_cancelados'] ?? 0) === 0
                ? 'Ninguno suspendido ni cancelado.'
                : `Suspende el ${porcentaje(ej['pct_suspendidos'] ?? 0, { fraccion: true, decimales: 1 })} y cancela el ${porcentaje(ej['pct_cancelados'] ?? 0, { fraccion: true, decimales: 1 })}.`}
            </p>
          </div>
        )}

        {sinCompetencia != null && (
          <div className="entidad-dato">
            <p className="exp-hero">
              <b className="cifra">{porcentaje(sinCompetencia, { fraccion: true })}</b>
              <span>adjudica sin competencia</span>
            </p>
            <p className="entidad-nota">
              De su gasto en modalidades competitivas, cuánto se cerró con un solo proponente.
              {captura?.['top1_share'] != null && (
                <> Su proveedor principal se lleva el{' '}
                  {porcentaje(captura['top1_share'], { fraccion: true, decimales: 1 })} del gasto.</>
              )}
            </p>
          </div>
        )}
      </div>

      {totales.length > 0 && (
        <div className="entidad-totales">
          {totales.map((t) => <span key={t}>{t}</span>)}
        </div>
      )}
    </>
  )
}


/* Expuesto solo para `Ficha.test.ts`. El reparto del titular es la clase de cifra que se rompe sin
   que nada falle: si un documento cae en dos tercios o en ninguno, el titular sigue escribiéndose
   y solo se nota sumando a mano lo que hay en pantalla. */
export const _pruebasFicha = { repartoEnPalabras, bloquesOferta, pasoRecorrido }
